import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FolderKanban, Sparkles, MessageSquare, FileText, TrendingUp,
  Bookmark, ArrowRight, Users, Building2, Plus, Shield,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import ProjectCard from "@/components/ProjectCard";
import MatchScore from "@/components/MatchScore";
import StreakWidget from "@/components/StreakWidget";
import ScoreBadge from "@/components/ScoreBadge";
import OnboardingTour from "@/components/OnboardingTour";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [mine, setMine] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [recs, setRecs] = useState([]);
  const [proposals, setProposals] = useState({ sent: [], received: [] });
  const [threads, setThreads] = useState([]);
  const [adminStats, setAdminStats] = useState(null);

  useEffect(() => {
    (async () => {
      if (user?.role === "creator") {
        const [{ data: p }, { data: r }, { data: pr }, { data: t }] = await Promise.all([
          api.get("/api/projects/mine"),
          api.get("/api/matches/recommendations"),
          api.get("/api/proposals"),
          api.get("/api/messages/threads"),
        ]);
        setMine(p); setRecs(r); setProposals(pr); setThreads(t);
      } else if (user?.role === "sponsor") {
        const [{ data: b }, { data: r }, { data: pr }, { data: t }] = await Promise.all([
          api.get("/api/briefs/mine"),
          api.get("/api/matches/recommendations"),
          api.get("/api/proposals"),
          api.get("/api/messages/threads"),
        ]);
        setBriefs(b); setRecs(r); setProposals(pr); setThreads(t);
      } else if (user?.role === "admin") {
        const { data } = await api.get("/api/admin/stats");
        setAdminStats(data);
      }
    })();
  }, [user]);

  if (!user) return null;

  const isCreator = user.role === "creator";
  const isSponsor = user.role === "sponsor";
  const isAdmin = user.role === "admin";

  return (
    <DashboardLayout
      title={t("dashboard.hi", { name: user.name.split(" ")[0] })}
      subtitle={
        isCreator
          ? t("dashboard.creator_intro")
          : isSponsor
            ? t("dashboard.sponsor_intro")
            : t("dashboard.admin_intro")
      }
      actions={
        <div className="flex items-center gap-3 flex-wrap">
          {(isCreator || isSponsor) && <ScoreBadge userId={user.id} size="md" />}
          {isCreator && (
            <Button data-testid="button-new-project" onClick={() => nav("/dashboard/proyectos/nuevo")} className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full px-5">
              <Plus size={16} className="mr-2" /> {t("dashboard.new_project")}
            </Button>
          )}
          {isSponsor && (
            <Button data-testid="button-new-brief" onClick={() => nav("/dashboard/briefs/nuevo")} className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full px-5">
              <Plus size={16} className="mr-2" /> {t("dashboard.new_brief")}
            </Button>
          )}
        </div>
      }
    >
      {(isCreator || isSponsor) && <OnboardingTour />}
      {isCreator && (
        <>
          <StreakWidget />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label={t("dashboard.quick_stats_projects")} value={mine.length} icon={FolderKanban} />
            <Kpi label={t("dashboard.quick_stats_matches")} value={recs.length} icon={Sparkles} />
            <Kpi label={t("dashboard.quick_stats_messages")} value={threads.reduce((a, t2) => a + t2.unread, 0)} icon={MessageSquare} tone="coral" />
            <Kpi label={t("sidebar.proposals")} value={proposals.sent.length + proposals.received.length} icon={FileText} />
          </div>

          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-petroleum">{t("dashboard.my_projects")}</h2>
              <Link to="/dashboard/proyectos" className="text-sm text-petroleum inline-flex items-center gap-1 hover:text-gold" data-testid="link-view-all-projects">
                {t("landing.featured_cta")} <ArrowRight size={14} />
              </Link>
            </div>
            {mine.length === 0 ? (
              <EmptyState
                title={t("dashboard.empty_projects_title")}
                text={t("dashboard.empty_projects_text")}
                cta={t("dashboard.empty_projects_cta")}
                onClick={() => nav("/dashboard/proyectos/nuevo")}
                testId="button-empty-create-project"
              />
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {mine.slice(0, 3).map((p) => <ProjectCard key={p.id} project={p} theme="light" />)}
              </div>
            )}
          </section>

          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-petroleum">{t("dashboard.reco_matches_title")}</h2>
              <Link to="/dashboard/coincidencias" className="text-sm text-petroleum inline-flex items-center gap-1 hover:text-gold">
                {t("dashboard.reco_matches_cta")} <ArrowRight size={14} />
              </Link>
            </div>
            {recs.length === 0 ? (
              <div className="kpi-card p-6 text-slate-500 text-sm">{t("dashboard.reco_matches_empty")}</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {recs.slice(0, 4).map((r) => (
                  <MatchListItem
                    key={`${r.project?.id || "p"}-${r.brief?.id || "b"}`}
                    rec={r}
                    viewer="creator"
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {isSponsor && (
        <>
          <StreakWidget />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Kpi label={t("dashboard.kpi_briefs")} value={briefs.length} icon={FolderKanban} />
            <Kpi label={t("dashboard.kpi_recommendations")} value={recs.length} icon={Sparkles} />
            <Kpi label={t("dashboard.kpi_messages")} value={threads.reduce((a, tt) => a + tt.unread, 0)} icon={MessageSquare} tone="coral" />
            <Kpi label={t("dashboard.kpi_proposals")} value={proposals.sent.length + proposals.received.length} icon={FileText} />
          </div>

          <section className="mt-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-petroleum">{t("dashboard.compatible_projects_title")}</h2>
              <Link to="/dashboard/recomendaciones" className="text-sm text-petroleum inline-flex items-center gap-1 hover:text-gold">
                {t("dashboard.reco_matches_cta")} <ArrowRight size={14} />
              </Link>
            </div>
            {recs.length === 0 ? (
              <EmptyState
                title={t("dashboard.empty_briefs_title")}
                text={t("dashboard.empty_briefs_text")}
                cta={t("dashboard.empty_briefs_cta")}
                onClick={() => nav("/dashboard/briefs/nuevo")}
                testId="button-empty-create-brief"
              />
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {recs.slice(0, 4).map((r) => (
                  <MatchListItem
                    key={`${r.project?.id || "p"}-${r.brief?.id || "b"}`}
                    rec={r}
                    viewer="sponsor"
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {isAdmin && adminStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label={t("dashboard.kpi_users")} value={adminStats.users} icon={Users} />
          <Kpi label={t("dashboard.kpi_creators")} value={adminStats.creators} icon={FolderKanban} />
          <Kpi label={t("dashboard.kpi_sponsors")} value={adminStats.sponsors} icon={Building2} />
          <Kpi label={t("dashboard.kpi_projects")} value={adminStats.projects} icon={FileText} />
          <Kpi label={t("dashboard.kpi_published")} value={adminStats.published_projects} icon={TrendingUp} />
          <Kpi label={t("dashboard.kpi_briefs_admin")} value={adminStats.briefs} icon={FolderKanban} />
          <Kpi label={t("dashboard.kpi_proposals")} value={adminStats.proposals} icon={FileText} />
          <Kpi label={t("dashboard.kpi_messages")} value={adminStats.messages} icon={MessageSquare} />
        </div>
      )}
    </DashboardLayout>
  );
}

function Kpi({ label, value, icon: Icon, tone }) {
  return (
    <div className="kpi-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
          <p className="text-3xl font-display text-petroleum mt-2">{value ?? 0}</p>
        </div>
        <span className={`inline-flex h-10 w-10 rounded-full items-center justify-center ${tone === "coral" ? "bg-coral/10 text-coral" : "bg-gold/10 text-gold"}`}>
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

function EmptyState({ title, text, cta, onClick, testId }) {
  return (
    <div className="kpi-card p-8 text-center">
      <h3 className="font-display text-2xl text-petroleum">{title}</h3>
      <p className="text-slate-500 mt-2 max-w-md mx-auto">{text}</p>
      <Button data-testid={testId} onClick={onClick} className="mt-4 bg-petroleum text-white hover:bg-petroleum/90 rounded-full px-6">
        <Plus size={16} className="mr-2" /> {cta}
      </Button>
    </div>
  );
}

function MatchListItem({ rec, viewer }) {
  const { t } = useTranslation();
  const project = rec.project;
  const brief = rec.brief;
  return (
    <div className="kpi-card p-5 flex gap-5 items-start">
      <MatchScore score={rec.score} size="md" />
      <div className="flex-1">
        <p className="text-xs uppercase tracking-widest text-slate-500">
          {viewer === "creator" ? t("dashboard.sponsor_suggested") : t("dashboard.project_suggested")}
        </p>
        <h4 className="font-display text-xl text-petroleum mt-1">
          {viewer === "creator" ? brief?.title : project?.title}
        </h4>
        <p className="text-sm text-slate-500 line-clamp-2 mt-1">
          {viewer === "creator" ? (brief?.description || "") : (project?.logline || "")}
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {rec.reasons?.slice(0, 3).map((r) => (
            <span key={r.label} className="text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase tracking-widest">
              {r.label}: {r.score}%
            </span>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          {viewer === "creator" ? (
            <Link to={`/dashboard/proyectos/${project?.id}`} className="text-xs text-petroleum hover:text-gold">{t("dashboard.edit_project")}</Link>
          ) : (
            <Link to={`/proyecto/${project?.id}`} className="text-xs text-petroleum hover:text-gold">{t("dashboard.view_project")}</Link>
          )}
        </div>
      </div>
    </div>
  );
}
